import React from 'react';

import {
  CART_PRODUCT_ADDED_KEY,
  CART_PRODUCT_ADDED_DEFAULT,
  HIDE_CART_WHEN_EMPTY_KEY,
  HIDE_CART_WHEN_EMPTY_DEFAULT,
  HIDE_CART_COUNT_KEY,
  HIDE_CART_COUNT_DEFAULT,
  CART_STATES,
  CART_TYPE_DROPDOWN_ON_OPEN_KEY,
  CART_TYPE_DROPDOWN_ON_OPEN,
  CART_TYPE_KEY,
  CART_TYPES,
  CART_ERRORS,
  DATA_ATTR_OPEN_PRODUCT,
  DATA_ATTR_OPEN_ON_HOVER,
  NODE_TYPE_COMMERCE_CART_WRAPPER,
  OPEN_STATE_DEFAULT,
  PREVIEW_ITEMS_DEFAULT,
  ANIMATION_EASING_DEFAULT,
  ANIMATION_DURATION_DEFAULT,
  ANIMATION_DURATION_KEY,
  DATA_ATTR_ANIMATION_EASING,
  DATA_ATTR_ANIMATION_DURATION,
  CART_QUERY,
  CART_TYPE,
  ALIGN_KEY,
  ALIGN_DEFAULT,
  MEMBERSHIP_PRODUCT_TYPE,
  CART_CHECKOUT_LOADING_TEXT_DEFAULT,
  LOADING_TEXT,
  PREVIEW_ITEMS_KEY,
  OPEN_STATE_KEY,
  ANIMATION_EASING_KEY,
} from '@packages/systems/commerce/constants';
import {
  getCartItemsSkuField,
  getCartItemsProductField,
  getProductNameField,
  getSkuImageField,
  getSkuPriceField,
} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {
  type PropEnum,
  ElementPreset,
  Plugins,
  Component,
  EnumType,
  Atom,
} from '@packages/systems/wfdl';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {
  SHARED_DYN_WRAPPER_CONSTRAINTS_ANCESTORS,
  SHARED_DYN_WRAPPER_CONSTRAINTS_DESCENDANTS,
} from '@packages/systems/core/constants/SharedConstraintRules';
import {buildQuickCheckoutActionsExpression} from './CommerceUtils';
import {getCommerceDataProperty} from './ContextUtils';
import {extraAtomFields} from '@packages/shared/constants/Atoms';
import {
  TTypeConstructor,
  TTypeApplication,
  EElement,
  EText,
  ERecord,
  EBoolean,
  EList,
  ENumber,
  EEnum,
} from '@packages/systems/wfdl-ast';
import {FieldProps} from '@packages/systems/dynamo/records/Field';
import {useSite} from '@packages/systems/render';
import {
  EvaluatedNodeData,
  getBooleanOrElse,
  getChildren,
  getInPojo,
  getNumber,
  getNumberOrElse,
  getString,
  getStringOrElse,
} from '@packages/utilities/data-utils';
import {RecordOf} from 'immutable';

export const Enum = (values: Array<string>): PropEnum => {
  return {
    type: EnumType,
    values,
  };
};

const CART_BINDING_ROOT_QUERY_PATH = ['database', 'commerceOrder'];

const getUserItemsCount = getCommerceDataProperty('userItemsCount');

const toBindingObject = (fieldRecord: RecordOf<FieldProps>) => {
  const {id, slug, type} = fieldRecord.toJS();
  return ERecord({id: EText(id), slug: EText(slug), type: EText(type)});
};

const CommerceCartWrapper = Atom.createRender(
  ({attributes, classes, data, context, generateAttributes, renderChild}) => {
    const site = useSite();

    // Update the default items count so that it does not exceed the total number of products
    const userItemsCount = getUserItemsCount(context);
    const defaultItemsCount =
      userItemsCount >= PREVIEW_ITEMS_DEFAULT
        ? PREVIEW_ITEMS_DEFAULT
        : userItemsCount;
    // Make sure that the node.data.temp.state does not exceed total possible preview items
    const previewItems = getNumber(
      getInPojo(data, ['temp', PREVIEW_ITEMS_KEY])
    );
    const previewItemsCount =
      typeof previewItems === 'number' && previewItems <= userItemsCount
        ? previewItems
        : defaultItemsCount;
    // Default cart state to empty if user has no products
    const cartStateDefault =
      userItemsCount > 0 ? CART_STATES.DEFAULT : CART_STATES.EMPTY;

    // With the new data structure when dropdown value in cartType is selected, the value assigned is not
    // anymore 'rightDropdown' or 'leftDropdown' but 'dropdown' therefore we will assign to cartType the value of align
    const cartType = getStringOrElse(
      getInPojo(data, [CART_TYPE_KEY]),
      CART_TYPES.MODAL
    );
    const align = getStringOrElse(getInPojo(data, [ALIGN_KEY]), ALIGN_DEFAULT);
    const isCartTypeDropdown = cartType.includes(CART_TYPES.DROPDOWN);
    const adjustedCartType = isCartTypeDropdown ? align : cartType;

    // TODO (ecommerce): Memoize commerce context mutations
    // create a new context, which we'll pass on down the tree via `renderChildren`
    const newContext = context
      // get Cart State from node.data.temp.state, and set it in context so it is available to descendants:
      .set(
        'tempState',
        getStringOrElse(getInPojo(data, ['temp', 'state']), cartStateDefault)
      )
      // get the previewItems state from node.data.temp.state and set it in the context so it is available to descendants:
      .set(
        'previewItems',
        getNumberOrElse(
          getInPojo(data, ['temp', PREVIEW_ITEMS_KEY]),
          PREVIEW_ITEMS_DEFAULT
        )
      )
      .set('previewItemsCount', previewItemsCount)
      // get Cart open/closed state from the node.data.temp.isOpen, and set it in context so it is available to descendants
      .set(
        'isOpen',
        getBooleanOrElse(
          getInPojo(data, ['temp', OPEN_STATE_KEY]),
          OPEN_STATE_DEFAULT
        )
      )
      .set('bindingPathPrefix', CART_BINDING_ROOT_QUERY_PATH)
      .set(CART_TYPE_KEY, adjustedCartType);

    const openOnProductAdd = getBooleanOrElse(
      getInPojo(data, ['commerce', CART_PRODUCT_ADDED_KEY]),
      CART_PRODUCT_ADDED_DEFAULT
    );
    const query = context.get('query') || '';

    const openOnProductAddAttributes = (
      openOnProductAdd ? attributes.set(DATA_ATTR_OPEN_PRODUCT, '') : attributes
    )
      .set(CART_TYPE, adjustedCartType)
      .set(CART_QUERY, query)
      .set('data-wf-page-link-href-prefix', site.get('pageLinkHrefPrefix', ''));

    const dropdownOpen = getString(
      getInPojo(data, [CART_TYPE_DROPDOWN_ON_OPEN_KEY])
    );
    const shouldAddHoverAttribute =
      (adjustedCartType === CART_TYPES.LEFT_DROPDOWN ||
        adjustedCartType === CART_TYPES.RIGHT_DROPDOWN) &&
      dropdownOpen === CART_TYPE_DROPDOWN_ON_OPEN.HOVER;

    let newAttributes = shouldAddHoverAttribute
      ? openOnProductAddAttributes.set(DATA_ATTR_OPEN_ON_HOVER, '')
      : openOnProductAddAttributes;

    const easingType = getString(
      getInPojo(data, ['commerce', ANIMATION_EASING_KEY])
    );
    if (easingType != null && easingType !== ANIMATION_EASING_DEFAULT) {
      newAttributes = newAttributes.set(DATA_ATTR_ANIMATION_EASING, easingType);
    }

    const duration = getNumber(
      getInPojo(data, ['commerce', ANIMATION_DURATION_KEY])
    );
    if (duration != null && duration !== ANIMATION_DURATION_DEFAULT) {
      newAttributes = newAttributes.set(
        DATA_ATTR_ANIMATION_DURATION,
        duration.toString()
      );
    }

    return (
      <div
        {...generateAttributes(newAttributes, classes)}
        data-node-type={NODE_TYPE_COMMERCE_CART_WRAPPER}
      >
        {getChildren(data).map((child: EvaluatedNodeData) =>
          renderChild(child, newContext)
        )}
      </div>
    );
  }
);

export const createCommerceCartWrapper: ElementPreset.ExpressionEscapeHatchFactory =
  ({collectionState}) => {
    if (collectionState === undefined) {
      throw Error('collectionState is required to create CommerceCartWrapper');
    }
    const {collections} = collectionState;
    const userItemsSkuField = getCartItemsSkuField(collections);
    const userItemsProductField = getCartItemsProductField(collections);

    // get the price field in sku and its value
    const userItemsSkuPriceField = getSkuPriceField(collections);
    // get the name field in product and its value
    const userItemsProductNameField = getProductNameField(collections);
    // get the url field in sku and its value
    const userItemsSkuImageField = getSkuImageField(collections);

    if (
      !userItemsSkuField ||
      !userItemsProductField ||
      !userItemsSkuPriceField ||
      !userItemsProductNameField ||
      !userItemsSkuImageField
    ) {
      throw new Error(
        'Missing fields data that is required to create a new Cart'
      );
    }

    return EElement({
      id: 'Commerce Cart Wrapper',
      type: qualify(
        Plugins.name('Commerce'),
        Component.name('CommerceCartWrapper')
      ),
      data: ERecord({
        tag: EEnum('div'),
        temp: ERecord({
          isOpen: EBoolean(false),
          state: EText(CART_STATES.DEFAULT),
          stateEmpty: EText(CART_STATES.EMPTY),
          previewItems: ENumber(3),
        }),
        align: EEnum(ALIGN_DEFAULT),
        commerce: ERecord({
          type: EText('cart-wrapper'),
          [CART_PRODUCT_ADDED_KEY]: EBoolean(CART_PRODUCT_ADDED_DEFAULT),
          duration: ENumber(300),
          easingType: EEnum('ease-out-quad'),
        }),
        cartType: EText(CART_TYPES.MODAL),
        openOn: EEnum(CART_TYPE_DROPDOWN_ON_OPEN.CLICK),
        children: EList([
          EElement({
            id: 'Commerce Cart Open Link',
            type: qualify(
              Plugins.name('Commerce'),
              Component.name('CommerceCartOpenLink')
            ),
            data: ERecord({
              block: EText('inline'),
              commerce: ERecord({
                type: EText('cart-open-link'),
                [HIDE_CART_COUNT_KEY]: EBoolean(HIDE_CART_COUNT_DEFAULT),
              }),
              children: EList([
                EElement({
                  id: 'Commerce Cart Open Link Icon',
                  type: qualify(
                    Plugins.name('Commerce'),
                    Component.name('CommerceCartOpenLinkIcon')
                  ),
                  data: ERecord({}),
                }),
                EElement({
                  id: 'Commerce Cart Wrapper Block',
                  type: qualify(Plugins.name('Basic'), Component.name('Block')),
                  data: ERecord({
                    tag: EEnum('div'),
                    text: EBoolean(true),
                    block: EText('inline'),
                    children: EList([
                      EElement({
                        id: 'Commerce Cart Wrapper String',
                        type: qualify(
                          Plugins.name('Basic'),
                          Component.name('String')
                        ),
                        data: EText('Cart'),
                      }),
                    ]),
                  }),
                }),
                EElement({
                  id: 'Commerce Cart Open Link Count',
                  type: qualify(
                    Plugins.name('Commerce'),
                    Component.name('CommerceCartOpenLinkCount')
                  ),
                  data: ERecord({
                    text: EBoolean(true),
                    commerce: ERecord({
                      type: EText('cart-open-link-count'),
                      [HIDE_CART_WHEN_EMPTY_KEY]: EBoolean(
                        HIDE_CART_WHEN_EMPTY_DEFAULT
                      ),
                    }),
                    dyn: ERecord({
                      bind: ERecord({
                        innerHTML: EList([
                          ERecord({
                            id: EText(''),
                            slug: EText('userItemsCount'),
                            type: EText('Number'),
                            filter: EText('numberPrecision|0|numberPrecision'),
                          }),
                        ]),
                      }),
                    }),
                  }),
                }),
              ]),
            }),
          }),
          EElement({
            id: 'Commerce Cart Container Wrapper',
            type: qualify(
              Plugins.name('Commerce'),
              Component.name('CommerceCartContainerWrapper')
            ),
            data: ERecord({
              commerce: ERecord({
                type: EText('cart-container-wrapper'),
              }),
              children: EList([
                EElement({
                  id: 'Commerce Cart Container',
                  type: qualify(
                    Plugins.name('Commerce'),
                    Component.name('CommerceCartContainer')
                  ),
                  data: ERecord({
                    commerce: ERecord({
                      type: EText('cart-container'),
                    }),
                    children: EList([
                      EElement({
                        id: 'Commerce Cart Header',
                        type: qualify(
                          Plugins.name('Commerce'),
                          Component.name('CommerceCartHeader')
                        ),
                        data: ERecord({
                          commerce: ERecord({
                            type: EText('cart-header'),
                          }),
                          children: EList([
                            EElement({
                              id: 'Commerce Cart Heading',
                              type: qualify(
                                Plugins.name('Commerce'),
                                Component.name('CommerceCartHeading')
                              ),
                              data: ERecord({
                                tag: EEnum('h4'),
                                commerce: ERecord({
                                  type: EText('cart-heading'),
                                }),
                                children: EList([
                                  EElement({
                                    id: 'Commerce Cart Wrapper String 2',
                                    type: qualify(
                                      Plugins.name('Basic'),
                                      Component.name('String')
                                    ),
                                    data: EText('Your Cart'),
                                  }),
                                ]),
                              }),
                            }),
                            EElement({
                              id: 'Commerce Cart Close Link',
                              type: qualify(
                                Plugins.name('Commerce'),
                                Component.name('CommerceCartCloseLink')
                              ),
                              data: ERecord({
                                block: EText('inline'),
                                commerce: ERecord({
                                  type: EText('cart-close-link'),
                                }),
                                children: EList([
                                  EElement({
                                    id: 'Commerce Cart Close Link Icon',
                                    type: qualify(
                                      Plugins.name('Commerce'),
                                      Component.name(
                                        'CommerceCartCloseLinkIcon'
                                      )
                                    ),
                                    data: ERecord({}),
                                  }),
                                ]),
                              }),
                            }),
                          ]),
                        }),
                      }),
                      EElement({
                        id: 'Commerce Cart Form Wrapper',
                        type: qualify(
                          Plugins.name('Commerce'),
                          Component.name('CommerceCartFormWrapper')
                        ),
                        data: ERecord({
                          commerce: ERecord({
                            type: EText('cart-form-wrapper'),
                          }),
                          children: EList([
                            EElement({
                              id: 'Commerce Cart Form',
                              type: qualify(
                                Plugins.name('Commerce'),
                                Component.name('CommerceCartForm')
                              ),
                              data: ERecord({
                                commerce: ERecord({
                                  type: EText('cart-form'),
                                }),
                                children: EList([
                                  EElement({
                                    id: 'Commerce Cart List',
                                    type: qualify(
                                      Plugins.name('Commerce'),
                                      Component.name('CommerceCartList')
                                    ),
                                    data: ERecord({
                                      tag: EEnum('div'),
                                      commerce: ERecord({
                                        type: EText('cart-list'),
                                      }),
                                      children: EList([
                                        EElement({
                                          id: 'Commerce Cart Item',
                                          type: qualify(
                                            Plugins.name('Commerce'),
                                            Component.name('CommerceCartItem')
                                          ),
                                          data: ERecord({
                                            commerce: ERecord({
                                              type: EText('cart-item'),
                                            }),
                                            children: EList([
                                              EElement({
                                                id: 'Commerce Cart Item Image',
                                                type: qualify(
                                                  Plugins.name('Commerce'),
                                                  Component.name(
                                                    'CommerceCartItemImage'
                                                  )
                                                ),
                                                data: ERecord({
                                                  commerce: ERecord({
                                                    type: EText(
                                                      'cart-item-image'
                                                    ),
                                                  }),
                                                  dyn: ERecord({
                                                    bind: ERecord({
                                                      src: EList([
                                                        toBindingObject(
                                                          userItemsSkuField
                                                        ),
                                                        toBindingObject(
                                                          userItemsSkuImageField
                                                        ),
                                                      ]),
                                                    }),
                                                  }),
                                                }),
                                              }),
                                              EElement({
                                                id: 'Commerce Cart Item Info',
                                                type: qualify(
                                                  Plugins.name('Commerce'),
                                                  Component.name(
                                                    'CommerceCartItemInfo'
                                                  )
                                                ),
                                                data: ERecord({
                                                  commerce: ERecord({
                                                    type: EText(
                                                      'cart-item-info'
                                                    ),
                                                  }),
                                                  children: EList([
                                                    EElement({
                                                      id: 'Commerce Cart Product Name',
                                                      type: qualify(
                                                        Plugins.name(
                                                          'Commerce'
                                                        ),
                                                        Component.name(
                                                          'CommerceCartProductName'
                                                        )
                                                      ),
                                                      data: ERecord({
                                                        tag: EEnum('div'),
                                                        text: EBoolean(true),
                                                        commerce: ERecord({
                                                          type: EText(
                                                            'cart-product-name'
                                                          ),
                                                        }),
                                                        dyn: ERecord({
                                                          bind: ERecord({
                                                            innerHTML: EList([
                                                              toBindingObject(
                                                                userItemsProductField
                                                              ),
                                                              toBindingObject(
                                                                userItemsProductNameField
                                                              ),
                                                            ]),
                                                          }),
                                                        }),
                                                        children: EList([]),
                                                      }),
                                                    }),
                                                    EElement({
                                                      id: 'Commerce Cart Wrapper Block 2',
                                                      type: qualify(
                                                        Plugins.name('Basic'),
                                                        Component.name('Block')
                                                      ),
                                                      data: ERecord({
                                                        tag: EEnum('div'),
                                                        text: EBoolean(true),
                                                        dyn: ERecord({
                                                          bind: ERecord({
                                                            innerHTML: EList([
                                                              toBindingObject(
                                                                userItemsSkuPriceField
                                                              ),
                                                            ]),
                                                          }),
                                                        }),
                                                        children: EList([]),
                                                      }),
                                                    }),
                                                    EElement({
                                                      id: 'Commerce Cart Option List',
                                                      type: qualify(
                                                        Plugins.name(
                                                          'Commerce'
                                                        ),
                                                        Component.name(
                                                          'CommerceCartOptionList'
                                                        )
                                                      ),
                                                      data: ERecord({
                                                        tag: EText('ul'),
                                                        children: EList([
                                                          EElement({
                                                            id: 'Commerce Cart Option List Item',
                                                            type: qualify(
                                                              Plugins.name(
                                                                'Commerce'
                                                              ),
                                                              Component.name(
                                                                'CommerceCartOptionListItem'
                                                              )
                                                            ),
                                                            data: ERecord({
                                                              tag: EText('li'),
                                                              children: EList([
                                                                EElement({
                                                                  id: 'Commerce Cart Option List Item Label',
                                                                  type: qualify(
                                                                    Plugins.name(
                                                                      'Commerce'
                                                                    ),
                                                                    Component.name(
                                                                      'CommerceCartOptionListItemLabel'
                                                                    )
                                                                  ),
                                                                  data: ERecord(
                                                                    {
                                                                      text: EBoolean(
                                                                        true
                                                                      ),
                                                                      dyn: ERecord(
                                                                        {
                                                                          bind: ERecord(
                                                                            {
                                                                              innerHTML:
                                                                                EList(
                                                                                  [
                                                                                    ERecord(
                                                                                      {
                                                                                        id: EText(
                                                                                          ''
                                                                                        ),
                                                                                        slug: EText(
                                                                                          'name'
                                                                                        ),
                                                                                        type: EText(
                                                                                          'PlainText'
                                                                                        ),
                                                                                      }
                                                                                    ),
                                                                                  ]
                                                                                ),
                                                                            }
                                                                          ),
                                                                        }
                                                                      ),
                                                                      children:
                                                                        EList(
                                                                          []
                                                                        ),
                                                                    }
                                                                  ),
                                                                }),
                                                                EElement({
                                                                  id: 'Commerce Cart Wrapper Block 3',
                                                                  type: qualify(
                                                                    Plugins.name(
                                                                      'Basic'
                                                                    ),
                                                                    Component.name(
                                                                      'Span'
                                                                    )
                                                                  ),
                                                                  data: ERecord(
                                                                    {
                                                                      text: EBoolean(
                                                                        true
                                                                      ),
                                                                      children:
                                                                        EList([
                                                                          EElement(
                                                                            {
                                                                              id: 'Commerce Cart Wrapper String 3',
                                                                              type: qualify(
                                                                                Plugins.name(
                                                                                  'Basic'
                                                                                ),
                                                                                Component.name(
                                                                                  'String'
                                                                                )
                                                                              ),
                                                                              data: EText(
                                                                                ': '
                                                                              ),
                                                                            }
                                                                          ),
                                                                        ]),
                                                                    }
                                                                  ),
                                                                }),
                                                                EElement({
                                                                  id: 'Commerce Cart Option List Item Value',
                                                                  type: qualify(
                                                                    Plugins.name(
                                                                      'Commerce'
                                                                    ),
                                                                    Component.name(
                                                                      'CommerceCartOptionListItemValue'
                                                                    )
                                                                  ),
                                                                  data: ERecord(
                                                                    {
                                                                      text: EBoolean(
                                                                        true
                                                                      ),
                                                                      dyn: ERecord(
                                                                        {
                                                                          bind: ERecord(
                                                                            {
                                                                              innerHTML:
                                                                                EList(
                                                                                  [
                                                                                    ERecord(
                                                                                      {
                                                                                        id: EText(
                                                                                          ''
                                                                                        ),
                                                                                        slug: EText(
                                                                                          'value'
                                                                                        ),
                                                                                        type: EText(
                                                                                          'PlainText'
                                                                                        ),
                                                                                      }
                                                                                    ),
                                                                                  ]
                                                                                ),
                                                                            }
                                                                          ),
                                                                        }
                                                                      ),
                                                                      children:
                                                                        EList(
                                                                          []
                                                                        ),
                                                                    }
                                                                  ),
                                                                }),
                                                              ]),
                                                            }),
                                                          }),
                                                        ]),
                                                      }),
                                                    }),
                                                    EElement({
                                                      id: 'Commerce Cart Remove Link',
                                                      type: qualify(
                                                        Plugins.name(
                                                          'Commerce'
                                                        ),
                                                        Component.name(
                                                          'CommerceCartRemoveLink'
                                                        )
                                                      ),
                                                      data: ERecord({
                                                        block: EText('inline'),
                                                        attr: ERecord({
                                                          href: EText('#'),
                                                          role: EText('button'),
                                                        }),
                                                        commerce: ERecord({
                                                          type: EText(
                                                            'cart-remove-link'
                                                          ),
                                                        }),
                                                        children: EList([
                                                          EElement({
                                                            id: 'Commerce Cart Wrapper Block 4',
                                                            type: qualify(
                                                              Plugins.name(
                                                                'Basic'
                                                              ),
                                                              Component.name(
                                                                'Block'
                                                              )
                                                            ),
                                                            data: ERecord({
                                                              tag: EEnum('div'),
                                                              text: EBoolean(
                                                                true
                                                              ),
                                                              children: EList([
                                                                EElement({
                                                                  id: 'Commerce Cart Wrapper String 4',
                                                                  type: qualify(
                                                                    Plugins.name(
                                                                      'Basic'
                                                                    ),
                                                                    Component.name(
                                                                      'String'
                                                                    )
                                                                  ),
                                                                  data: EText(
                                                                    'Remove'
                                                                  ),
                                                                }),
                                                              ]),
                                                            }),
                                                          }),
                                                        ]),
                                                      }),
                                                    }),
                                                  ]),
                                                }),
                                              }),
                                              EElement({
                                                id: 'Commerce Cart Quantity',
                                                type: qualify(
                                                  Plugins.name('Commerce'),
                                                  Component.name(
                                                    'CommerceCartQuantity'
                                                  )
                                                ),
                                                data: ERecord({
                                                  attr: ERecord({
                                                    'aria-label':
                                                      EText('Update quantity'),
                                                  }),
                                                  commerce: ERecord({
                                                    type: EText(
                                                      'cart-quantity'
                                                    ),
                                                  }),
                                                  dyn: ERecord({
                                                    condition: ERecord({
                                                      class: ERecord({
                                                        false: ERecord({
                                                          'w-condition-invisible':
                                                            ERecord({
                                                              fields: ERecord({
                                                                'product:ec-product-type':
                                                                  ERecord({
                                                                    ne: EText(
                                                                      MEMBERSHIP_PRODUCT_TYPE.id
                                                                    ),
                                                                  }),
                                                              }),
                                                            }),
                                                        }),
                                                      }),
                                                    }),
                                                  }),
                                                }),
                                              }),
                                            ]),
                                          }),
                                        }),
                                      ]),
                                    }),
                                  }),
                                  EElement({
                                    id: 'Commerce Cart Footer',
                                    type: qualify(
                                      Plugins.name('Commerce'),
                                      Component.name('CommerceCartFooter')
                                    ),
                                    data: ERecord({
                                      commerce: ERecord({
                                        type: EText('cart-footer'),
                                      }),
                                      children: EList([
                                        EElement({
                                          id: 'Commerce Cart Line Item',
                                          type: qualify(
                                            Plugins.name('Commerce'),
                                            Component.name(
                                              'CommerceCartLineItem'
                                            )
                                          ),
                                          data: ERecord({
                                            attr: ERecord({
                                              'aria-atomic': EBoolean(true),
                                              'aria-live': EText('polite'),
                                            }),
                                            commerce: ERecord({
                                              type: EText('cart-line-item'),
                                            }),
                                            children: EList([
                                              EElement({
                                                id: 'Commerce Cart Wrapper Block 5',
                                                type: qualify(
                                                  Plugins.name('Basic'),
                                                  Component.name('Block')
                                                ),
                                                data: ERecord({
                                                  tag: EEnum('div'),
                                                  text: EBoolean(true),
                                                  children: EList([
                                                    EElement({
                                                      id: 'Commerce Cart Wrapper String 5',
                                                      type: qualify(
                                                        Plugins.name('Basic'),
                                                        Component.name('String')
                                                      ),
                                                      data: EText('Subtotal'),
                                                    }),
                                                  ]),
                                                }),
                                              }),
                                              EElement({
                                                id: 'Commerce Cart Order Value',
                                                type: qualify(
                                                  Plugins.name('Commerce'),
                                                  Component.name(
                                                    'CommerceCartOrderValue'
                                                  )
                                                ),
                                                data: ERecord({
                                                  tag: EEnum('div'),
                                                  text: EBoolean(true),
                                                  commerce: ERecord({
                                                    type: EText(
                                                      'cart-order-value'
                                                    ),
                                                  }),
                                                  dyn: ERecord({
                                                    bind: ERecord({
                                                      innerHTML: EList([
                                                        ERecord({
                                                          id: EText(''),
                                                          slug: EText(
                                                            'subtotal'
                                                          ),
                                                          type: EText(
                                                            'CommercePrice'
                                                          ),
                                                        }),
                                                      ]),
                                                    }),
                                                  }),
                                                  children: EList([]),
                                                }),
                                              }),
                                            ]),
                                          }),
                                        }),
                                        EElement({
                                          id: 'Commerce Cart Checkout Actions',
                                          type: qualify(
                                            Plugins.name('Commerce'),
                                            Component.name(
                                              'CommerceCartCheckoutActions'
                                            )
                                          ),
                                          data: ERecord({
                                            commerce: ERecord({
                                              type: EText(
                                                'cart-checkout-actions'
                                              ),
                                            }),
                                            children: EList([
                                              buildQuickCheckoutActionsExpression(),
                                              EElement({
                                                id: 'Commerce Cart Checkout Button',
                                                type: qualify(
                                                  Plugins.name('Commerce'),
                                                  Component.name(
                                                    'CommerceCartCheckoutButton'
                                                  )
                                                ),
                                                data: ERecord({
                                                  attr: ERecord({
                                                    href: EText('/checkout'),
                                                    value: EText(
                                                      'Continue to Checkout'
                                                    ),
                                                  }),
                                                  commerce: ERecord({
                                                    type: EText(
                                                      'cart-checkout-button'
                                                    ),
                                                    [LOADING_TEXT]: EText(
                                                      CART_CHECKOUT_LOADING_TEXT_DEFAULT
                                                    ),
                                                  }),
                                                }),
                                              }),
                                            ]),
                                          }),
                                        }),
                                      ]),
                                    }),
                                  }),
                                ]),
                              }),
                            }),
                            EElement({
                              id: 'Commerce Cart Empty State',
                              type: qualify(
                                Plugins.name('Commerce'),
                                Component.name('CommerceCartEmptyState')
                              ),
                              data: ERecord({
                                commerce: ERecord({
                                  type: EText('cart-empty-state'),
                                }),
                                children: EList([
                                  EElement({
                                    id: 'Commerce Cart Wrapper Block 7',
                                    type: qualify(
                                      Plugins.name('Basic'),
                                      Component.name('Block')
                                    ),
                                    data: ERecord({
                                      xattr: EList([
                                        ERecord({
                                          name: EText('aria-live'),
                                          value: EText('polite'),
                                        }),
                                        ERecord({
                                          name: EText('aria-label'),
                                          value: EText('This cart is empty'),
                                        }),
                                      ]),
                                      tag: EEnum('div'),
                                      text: EBoolean(true),
                                      children: EList([
                                        EElement({
                                          id: 'Commerce Cart Wrapper String 7',
                                          type: qualify(
                                            Plugins.name('Basic'),
                                            Component.name('String')
                                          ),
                                          data: EText('No items found.'),
                                        }),
                                      ]),
                                    }),
                                  }),
                                ]),
                              }),
                            }),
                            EElement({
                              id: 'Commerce Cart Error State',
                              type: qualify(
                                Plugins.name('Commerce'),
                                Component.name('CommerceCartErrorState')
                              ),
                              data: ERecord({
                                attr: ERecord({
                                  'aria-live': EText('assertive'),
                                }),
                                commerce: ERecord({
                                  type: EText('cart-error-state'),
                                }),
                                children: EList([
                                  EElement({
                                    id: 'Commerce Cart Error Msg',
                                    type: qualify(
                                      Plugins.name('Commerce'),
                                      Component.name('CommerceCartErrorMsg')
                                    ),
                                    data: ERecord({
                                      temp: ERecord({index: ENumber(0)}),
                                      tag: EText('label'),
                                      text: EBoolean(true),
                                      commerce: ERecord({
                                        type: EText('cart-error-msg'),
                                      }),
                                      texts: EList([
                                        ERecord({
                                          id: EText(CART_ERRORS.QUANTITY.id),
                                          name: EText(
                                            CART_ERRORS.QUANTITY.name
                                          ),
                                          copy: EText(
                                            CART_ERRORS.QUANTITY.copy
                                          ),
                                          category: EText(
                                            CART_ERRORS.QUANTITY.category.label
                                          ),
                                        }),
                                        ERecord({
                                          id: EText(CART_ERRORS.GENERAL.id),
                                          name: EText(CART_ERRORS.GENERAL.name),
                                          copy: EText(CART_ERRORS.GENERAL.copy),
                                          category: EText(
                                            CART_ERRORS.GENERAL.category.label
                                          ),
                                        }),
                                        ERecord({
                                          id: EText(CART_ERRORS.CHECKOUT.id),
                                          name: EText(
                                            CART_ERRORS.CHECKOUT.name
                                          ),
                                          copy: EText(
                                            CART_ERRORS.CHECKOUT.copy
                                          ),
                                          category: EText(
                                            CART_ERRORS.CHECKOUT.category.label
                                          ),
                                        }),
                                        ERecord({
                                          id: EText(
                                            CART_ERRORS.CART_ORDER_MIN.id
                                          ),
                                          name: EText(
                                            CART_ERRORS.CART_ORDER_MIN.name
                                          ),
                                          copy: EText(
                                            CART_ERRORS.CART_ORDER_MIN.copy
                                          ),
                                          category: EText(
                                            CART_ERRORS.CART_ORDER_MIN.category
                                              .label
                                          ),
                                        }),
                                        ERecord({
                                          id: EText(
                                            CART_ERRORS.SUBSCRIPTION_ERROR.id
                                          ),
                                          name: EText(
                                            CART_ERRORS.SUBSCRIPTION_ERROR.name
                                          ),
                                          copy: EText(
                                            CART_ERRORS.SUBSCRIPTION_ERROR.copy
                                          ),
                                          category: EText(
                                            CART_ERRORS.SUBSCRIPTION_ERROR
                                              .category.label
                                          ),
                                        }),
                                      ]),
                                    }),
                                  }),
                                ]),
                              }),
                            }),
                          ]),
                        }),
                      }),
                    ]),
                  }),
                }),
              ]),
            }),
          }),
        ]),
      }),
    });
  };

const config = Atom.create({
  displayName: 'Cart',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Commerce'), iconName('ElementCart')),
  },
  showEmpty: true,
  showSettingsInDescendants: true,
  constraints: {
    wraps: ['CommerceCartOpenLink', 'CommerceCartContainerWrapper'],
    ancestors: [
      {is: 'NavbarMenu', rule: 'Forbid'},
      {is: 'CommerceAddToCartWrapper', rule: 'Forbid'},
      ...SHARED_DYN_WRAPPER_CONSTRAINTS_ANCESTORS,
    ],
    descendants: [
      {is: 'HtmlEmbed', rule: 'Forbid'},
      ...SHARED_DYN_WRAPPER_CONSTRAINTS_DESCENDANTS,
    ],
  },
  props: {
    cartType: Enum([
      CART_TYPES.MODAL,
      CART_TYPES.LEFT_SIDEBAR,
      CART_TYPES.RIGHT_SIDEBAR,
      CART_TYPES.DROPDOWN,
    ]),
  },
  style: {
    base: {
      main: {
        noPseudo: {
          display: 'inline-block',
          position: 'relative',
        },
      },
    },
  },
  lockedStyles: {
    display: {
      when: true,
      message: 'The cart wrapper `display` property can’t be changed.',
    },
    position: {
      when: true,
      message: 'The cart wrapper `position` property can’t be changed.',
    },
  },
  render: CommerceCartWrapper,
  dataType: TTypeApplication({
    con: TTypeConstructor(
      qualify(Plugins.name('Commerce'), typeName('CartType'))
    ),
    arg: extraAtomFields,
  }),
});

export default config;
